import DOMPurify from "dompurify";

/**
 * Operator-authored notices (e.g. the Windows license warning written on an OS image) may contain
 * a small subset of HTML. These are the only tags and attributes that survive — inline
 * formatting, paragraphs, lists and links. Scripts, styles, images, iframes, forms and event
 * handlers are all stripped, and DOMPurify also drops unsafe link schemes (javascript:).
 *
 * Keep this list identical to wi-cloud-client/src/lib/noticeHtml.ts, which renders the same
 * notice to customers — the form preview is only honest while the two agree.
 */
export const NOTICE_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "code",
  "span",
  "a",
  "ul",
  "ol",
  "li",
];
const ALLOWED_ATTR = ["href"];

const HTML_TAG = /<\/?[a-z][\s\S]*?>/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Turns an operator-authored notice into HTML that is safe to inject.
 *
 * Plain text keeps its line breaks; anything containing a tag is treated as HTML and sanitised to
 * the allowlist above. Links always open in a new tab without access to `window.opener`.
 */
export function toSafeNoticeHtml(message: string): string {
  const source = HTML_TAG.test(message)
    ? message
    : escapeHtml(message).replace(/\r?\n/g, "<br>");

  const fragment = DOMPurify.sanitize(source, {
    ALLOWED_TAGS: NOTICE_ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });

  fragment.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });

  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML;
}

/** Styles for the elements the allowlist lets through — mirrors the client console's copy. */
export const NOTICE_HTML_CLASSES =
  "wrap-break-word [&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_li]:mt-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_p+p]:mt-2 [&_p+ul]:mt-1.5 [&_p+ol]:mt-1.5 [&_ul+p]:mt-2 [&_ol+p]:mt-2 [&_strong]:font-semibold [&_b]:font-semibold";
